import type { SgWorkspace } from "./api/types.ts";
import { useEventBusDispatcher } from "@sg-protect/federation-runtime";
import { SG_WORKSPACE_CHANGED_EVENT } from "./events.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

const WORKSPACE_ID_KEY = "sg-workspace-id";
const WORKSPACE_QUERY_PARAM = "wid";

interface GetDefaultWorkspaceParams {
    workspaceId: string;
}

export function getDefaultWorkspace(workspaces: SgWorkspace[], params: GetDefaultWorkspaceParams) {
    return (
        workspaces.find(w => w.id === params.workspaceId) ??
        workspaces.find(w => w.id === new URLSearchParams(window.location.search).get(WORKSPACE_QUERY_PARAM)) ??
        workspaces.find(w => w.id === localStorage.getItem(WORKSPACE_ID_KEY)) ??
        workspaces[0]
    );
}

// This hook manages the current workspace state and provides a function to change it
export function useCurrentWorkspace(workspaces: SgWorkspace[]) {
    const { workspaceId = "" } = useParams();
    const [currentWorkspace, setCurrentWorkspace] = useState<SgWorkspace>();
    const dispatch = useEventBusDispatcher();

    if (!currentWorkspace && workspaces.length > 0) {
        setCurrentWorkspace(() => getDefaultWorkspace(workspaces, { workspaceId }));
    }

    const changeWorkspace = useCallback(
        (workspace: SgWorkspace) => {
            if (currentWorkspace?.id !== workspace.id) {
                // No setCurrentWorkspace here: the page would re-render as the new workspace (name, banner, session)
                // before WorkspaceBoundary's full-page reload lands, and Back would restore it that way from bfcache.
                localStorage.setItem(WORKSPACE_ID_KEY, workspace.id);
                dispatch(SG_WORKSPACE_CHANGED_EVENT, { workspace });
            }
        },
        [currentWorkspace?.id, dispatch]
    );

    useEffect(() => {
        if (currentWorkspace) {
            localStorage.setItem(WORKSPACE_ID_KEY, currentWorkspace.id);
        }
    }, [currentWorkspace]);

    return useMemo(() => ({ currentWorkspace, changeWorkspace }), [currentWorkspace, changeWorkspace]);
}
